import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
  HostListener,
  HostBinding,
  signal,
} from '@angular/core';
import { take } from "rxjs/operators";
import { ConsultantsFeatures } from "@my7n/features";
import { AuthorizationService } from "../../../../services/authorization.service";
import { Observable } from "rxjs";

// Interfaces
import { INotificationItem, NotificationItemType } from '../../../../interfaces/notification-item';
// Facades
import { NotificationsFacade } from '../../../../services/facades/notifications-facade.service';

@Component({
    selector: 'notifications-list-element',
    templateUrl: './notifications-list-element.component.html',
    styleUrls: ['./notifications-list-element.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NotificationsListElementComponent implements OnChanges {
  @HostBinding('class.notification-read') get isNotificationRead() {
    return this.notification && this.notification.IsVisited;
  }
  @Input() notification: INotificationItem;
  @Output() checkDetailsClicked = new EventEmitter<null>();

  hideReason = signal(false);
  hasAction = signal(true); // By default every notification has action -> redirect on click

  isHovered = false;
  isOtherType: boolean;
  hasAccessToConsultantProfile$: Observable<boolean>;

  urlBase: string;
  urlParams: {
    [k: string]: any;
  };

  @HostListener('mouseenter') onMouseEnter() {
    this.isHovered = true;
  }
  @HostListener('mouseleave') onMouseLeave() {
    this.isHovered = false;
  }

  get consultantItemCvViewFeature() {
    return ConsultantsFeatures.ItemCvView;
  }

  constructor(
    private notificationsFacade: NotificationsFacade,
    private authorizationService: AuthorizationService
  ) {
    this.hasAccessToConsultantProfile$ = this.authorizationService
      .can(this.consultantItemCvViewFeature)
  }

  /**
   * Important!
   * Below logic must be execute on changes because this component is used by virtual scroll.
   * Virtual scroll uses cache for list item components and ngOnInit is not always executed.
   */
  ngOnChanges(changes: SimpleChanges) {
    this.isOtherType = this.notification.Type === NotificationItemType.OTHER;
    if (!this.isOtherType) {
      this.hasAccessToConsultantProfile$
        .pipe(take(1))
        .subscribe((hasAccess) => this.setNotificationUrlParts(hasAccess));
    }
  }

  setNotificationUrlParts(hasAccessToConsultantProfile: boolean) {
    const urlParams = {};
    let urlBase;

    switch (this.notification.Type) {
      case NotificationItemType.EVENT:
        urlBase = `/events/${this.notification.ExternalId}`;
        break;
      case NotificationItemType.NEWS:
        urlBase = `/news/${this.notification.ExternalId}`;
        break;
      case NotificationItemType.REFERRAL:
        urlBase = `/referrals/overview`;
        urlParams['id'] = this.notification.ExternalId;
        break;
      case NotificationItemType.CV:
        this.hideReason.set(true);
        if (this.notification.Reason === 'CvParsingCompleted') {
          if (hasAccessToConsultantProfile) {
            urlBase = `/agent/consultant/cv/${this.notification.ExternalId}`;
          } else {
            urlBase = `/cv/${this.notification.ExternalId}`;
          }
        } else {
          if (hasAccessToConsultantProfile) {
            this.hasAction.set(false); // [Agent] Cv parsing failed -> no redirect on click
          } else {
            urlBase = `/cv`;
          }
        }
        break;
      default:
        console.error(`[NotificationsListElement] Wrong notification type or not provided at all. Notification Id: ${this.notification.Id}`);
        this.hideReason.set(true);
        return null;
    }

    this.urlBase = urlBase;
    this.urlParams = urlParams;
  }

  handleClick($event) {
    if (!this.notification.IsVisited) {
      this.notificationsFacade.notificationItemRead(this.notification.Id);
    }
    // Prevent from double router navigate calling
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    this.checkDetailsClicked.emit();
  }
}
